import firebaseConfig from 'constants/firebase-config';
import { getApp, getApps, initializeApp } from 'firebase/app';
import { getMessaging, getToken } from 'firebase/messaging';
import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';

export type Props = {};

const FirebaseCloudMessagingTokenContext = createContext<string | null>(null);

export default function FirebaseProvider({ children }: React.PropsWithChildren<Props>) {
  const [token, setToken] = useState<string | null>(null);

  const registerServiceWorkerAndRequestToken = useCallback(async () => {
    if (
      'serviceWorker' in navigator &&
      'Notification' in window &&
      Notification.permission === 'granted'
    ) {
      const firebaseApp = !getApps().length ? initializeApp(firebaseConfig) : getApp(); // gets the default initialized app or, if not found, intiializes a new one
      const messaging = getMessaging(firebaseApp);

      const serviceWorkerRegistration = await navigator.serviceWorker.register(
        new URL('./worker.ts', import.meta.url),
        {
          scope: '/',
        },
      );

      await navigator.serviceWorker.ready; // Ensure the service worker is ready before requesting the token

      return await getToken(messaging, {
        vapidKey: process.env.NEXT_PUBLIC_FIREBASE_VAPID_ID, // Our Firebase project's VAPID key
        serviceWorkerRegistration: serviceWorkerRegistration,
      });
    }
    return null;
  }, []);

  useEffect(() => {
    registerServiceWorkerAndRequestToken().then((fcmToken) => {
      setToken(fcmToken);
    });
    if ('permissions' in navigator) {
      navigator.permissions.query({ name: 'notifications' }).then((notificationPerm) => {
        notificationPerm.onchange = () => {
          registerServiceWorkerAndRequestToken().then((fcmToken) => {
            setToken(fcmToken);
          });
        };
      });
    }
  }, [registerServiceWorkerAndRequestToken, setToken]);

  return (
    <FirebaseCloudMessagingTokenContext.Provider value={token}>
      {children}
    </FirebaseCloudMessagingTokenContext.Provider>
  );
}

export function useFirebaseCloudMessagingToken(): string | null {
  const context = useContext(FirebaseCloudMessagingTokenContext);
  if (context === undefined) {
    throw new Error('useFirebaseCloudMessagingToken must be used within a FirebaseProvider');
  }
  return context;
}
